import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@/app/store/store';
import {
  BudgetInfo,
  BudgetPart,
  Device,
  Photo,
  ReceptionDraft,
  ReceptionWizardState,
} from '@/types/app/reception';

/**
 * Selector base que devuelve todo el slice del wizard.
 */
export const selectReceptionWizardState = (state: RootState): ReceptionWizardState =>
  state.receptionWizard;

/**
 * Obtiene el draft activo o undefined si aún no se inicia el flujo.
 */
export const selectActiveDraft = (state: RootState): ReceptionDraft | undefined => {
  const wizard = selectReceptionWizardState(state);
  return wizard.activeDraftId ? wizard.draftsById[wizard.activeDraftId] : undefined;
};

/**
 * Devuelve el id del dispositivo actualmente editable.
 */
export const selectEditableDeviceId = (state: RootState): string | undefined => {
  const draft = selectActiveDraft(state);
  return draft?.devices.activeDeviceId;
};

/**
 * Entrega una lista ordenada de dispositivos según fueron añadidos.
 */
const EMPTY_DEVICES: Device[] = [];

export const selectDevicesList = createSelector(selectActiveDraft, (draft): Device[] => {
  if (!draft) return EMPTY_DEVICES;
  const devices = draft.devices.allIds
    .map((id) => draft.devices.byId[id])
    .filter((device): device is Device => Boolean(device));
  return devices.length ? devices : EMPTY_DEVICES;
});

/**
 * Recupera un dispositivo específico por id.
 */
export const selectDeviceById = (state: RootState, deviceId: string): Device | undefined => {
  const draft = selectActiveDraft(state);
  return draft?.devices.byId[deviceId];
};

/**
 * Atajo para obtener el dispositivo activo.
 */
export const selectActiveDevice = (state: RootState): Device | undefined => {
  const draft = selectActiveDraft(state);
  if (!draft?.devices.activeDeviceId) return undefined;
  return draft.devices.byId[draft.devices.activeDeviceId];
};

/**
 * Devuelve el índice 1-based del dispositivo activo. Si no hay uno seleccionado,
 * regresa el siguiente consecutivo (allIds.length + 1) para numerar el “nuevo” equipo.
 */
export const selectActiveDevicePosition = (state: RootState): number => {
  const draft = selectActiveDraft(state);
  if (!draft) return 1;
  const { activeDeviceId, allIds } = draft.devices;
  if (!activeDeviceId) {
    return allIds.length + 1;
  }
  const idx = allIds.indexOf(activeDeviceId);
  return idx >= 0 ? idx + 1 : allIds.length + 1;
};

/**
 * Lista todas las fotos asociadas a un dispositivo.
 */
const buildDevicePhotos = (draft: ReceptionDraft | undefined, deviceId: string): Photo[] => {
  if (!draft) return EMPTY_PHOTOS;
  const device = draft.devices.byId[deviceId];
  if (!device) return EMPTY_PHOTOS;
  const photos = device.photos
    .map((photoId) => draft.photos.byId[photoId])
    .filter((photo): photo is Photo => Boolean(photo));
  if (photos.length === 0) return EMPTY_PHOTOS;
  return photos.sort((a, b) => {
    const aSlot = typeof a.slotIndex === 'number' ? a.slotIndex : Number.MAX_SAFE_INTEGER;
    const bSlot = typeof b.slotIndex === 'number' ? b.slotIndex : Number.MAX_SAFE_INTEGER;
    if (aSlot !== bSlot) return aSlot - bSlot;
    return 0;
  }); // Ordena primero por slot asignado para mapear fácilmente a la UI.
};

const EMPTY_PHOTOS: Photo[] = [];

export const selectDevicePhotos = (state: RootState, deviceId: string): Photo[] => {
  const draft = selectActiveDraft(state);
  return buildDevicePhotos(draft, deviceId);
};

export const makeSelectDevicePhotos = (deviceId: string) =>
  createSelector(selectActiveDraft, (draft) => buildDevicePhotos(draft, deviceId));

export const selectEmptyDevicePhotos = (_state: RootState) => EMPTY_PHOTOS;

/**
 * Obtiene el presupuesto de un dispositivo si existe.
 */
export const selectDeviceBudget = (state: RootState, deviceId: string): BudgetInfo | undefined => {
  const device = selectDeviceById(state, deviceId);
  return device?.budget;
};

/**
 * Devuelve todas las piezas registradas en el presupuesto del dispositivo.
 */
export const selectBudgetParts = (state: RootState, deviceId: string): BudgetPart[] => {
  const budget = selectDeviceBudget(state, deviceId);
  return budget?.parts ?? [];
};

export const selectBudgetPartsByDevice = (state: RootState) => {
  const draft = selectActiveDraft(state);
  if (!draft) return {};
  const map: Record<string, BudgetPart[]> = {};
  draft.devices.allIds.forEach((id) => {
    const device = draft.devices.byId[id];
    if (!device) return;
    map[id] = device.budget?.parts ?? [];
  });
  return map;
};

/**
 * Exposición de las claves de idempotencia guardadas en el draft.
 */
export const selectIdempotency = (state: RootState) => {
  const draft = selectActiveDraft(state);
  return draft?.idempotency ?? { current: undefined, last: undefined };
};
